import React, { useState } from 'react';
import * as api from '../api';
import { Music, Upload, Trash2, Sparkles, AlertCircle, RefreshCw } from 'lucide-react';
import CyberBadge from './common/CyberBadge';

const resolveAdminMediaUri = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=300&q=80';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  return `${api.API_ORIGIN || 'https://www.entermtrx.com'}${url.startsWith('/') ? url : `/${url}`}`;
};

export default function MusicPublisher({ publishedTracks = [], loadTracks, showTemporarySuccess }) {
  const [trackTitle, setTrackTitle] = useState('');
  const [trackArtist, setTrackArtist] = useState('');
  const [trackAudioFile, setTrackAudioFile] = useState(null);
  const [trackAudioUrl, setTrackAudioUrl] = useState('');
  const [trackCoverFile, setTrackCoverFile] = useState(null);
  const [trackCoverImage, setTrackCoverImage] = useState('');
  const [trackGenre, setTrackGenre] = useState('HIP-HOP');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [publisherError, setPublisherError] = useState('');

  const handleAudioChange = (file) => {
    if (!file) {
      setTrackAudioFile(null);
      setAudioPreviewUrl('');
      return;
    }
    setTrackAudioFile(file);
    const objectUrl = URL.createObjectURL(file);
    setAudioPreviewUrl(objectUrl);
  };

  const handleImageChange = (file) => {
    if (!file) {
      setTrackCoverFile(null);
      setImagePreviewUrl('');
      return;
    }
    setTrackCoverFile(file);
    const objectUrl = URL.createObjectURL(file);
    setImagePreviewUrl(objectUrl);
  };

  const handlePublishTrack = async (e) => {
    e.preventDefault();
    setPublisherError('');
    if (!trackTitle.trim()) {
      alert('Please enter a track title');
      return;
    }
    setIsSubmitting(true);
    try {
      let payload;
      if (trackAudioFile || trackCoverFile) {
        payload = new FormData();
        payload.append('title', trackTitle.trim());
        payload.append('artist', trackArtist.trim() || 'Admin Featured Artist');
        payload.append('genre', trackGenre);
        if (trackAudioFile) {
          payload.append('audio', trackAudioFile);
        } else if (trackAudioUrl.trim()) {
          payload.append('audioUrl', trackAudioUrl.trim());
        }
        if (trackCoverFile) {
          payload.append('image', trackCoverFile);
        } else if (trackCoverImage.trim()) {
          payload.append('image', trackCoverImage.trim());
        }
      } else {
        payload = {
          title: trackTitle.trim(),
          artist: trackArtist.trim() || 'Admin Featured Artist',
          audioUrl: trackAudioUrl.trim() || 'https://raw.githubusercontent.com/rafaelreis-hotmart/Audio-Sample-files/master/sample.mp3',
          image: trackCoverImage.trim() || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=500&q=80',
          genre: trackGenre,
        };
      }

      const res = await api.createTrack(payload);
      if (res.success) {
        showTemporarySuccess('🎵 Audio Track Published! Streaming live in mobile app floating bubble player.');
        setTrackTitle('');
        setTrackArtist('');
        setTrackAudioFile(null);
        setTrackAudioUrl('');
        setTrackCoverFile(null);
        setTrackCoverImage('');
        setAudioPreviewUrl('');
        setImagePreviewUrl('');
        if (loadTracks) loadTracks();
      }
    } catch (err) {
      console.error('❌ Track Publish Error:', err);
      setPublisherError(err.message || 'Failed to publish track. Check backend connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTrack = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}" from the mobile app?`)) return;
    try {
      const res = await api.deleteTrack(id);
      if (res.success) {
        showTemporarySuccess(`Track "${title}" deleted successfully!`);
        if (loadTracks) loadTracks();
      }
    } catch (err) {
      alert(err.message || 'Failed to delete track');
    }
  };

  const tracksList = Array.isArray(publishedTracks) ? publishedTracks : [];

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Page Header Banner */}
      <div>
        <h1 className="page-title" style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Music size={28} style={{ color: 'var(--neon-purple)' }} /> Music & Anthem Publisher
        </h1>
        <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
          Publish underground audio tracks & anthems live to the mobile app floating bubble audio player.
        </p>
      </div>

      {publisherError && (
        <div style={{ padding: '14px 18px', borderRadius: 'var(--radius-md)', background: 'rgba(244, 63, 94, 0.15)', border: '1px solid rgba(244, 63, 94, 0.35)', color: 'var(--neon-rose)', fontSize: '0.88rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertCircle size={18} />
          <span>{publisherError}</span>
        </div>
      )}

      {/* Publish Form Card */}
      <div className="cyber-card" style={{ padding: '28px' }}>
        <span className="corner-tl" />
        <span className="corner-tr" />
        <span className="corner-bl" />
        <span className="corner-br" />

        <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Sparkles size={20} style={{ color: 'var(--neon-purple)' }} /> Publish Audio Track to Floating Bubble Player
        </h3>

        <form onSubmit={handlePublishTrack} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          {/* Track Title */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Track Title *</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Malwa Underground Anthem 2026"
              value={trackTitle}
              onChange={(e) => setTrackTitle(e.target.value)}
              required
            />
          </div>

          {/* Artist Name */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Artist / Producer Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. MC Shadow x DJ Rony"
              value={trackArtist}
              onChange={(e) => setTrackArtist(e.target.value)}
            />
          </div>

          {/* Audio Dropzone */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>🎵 Upload Audio MP3 File</label>
            <div
              style={{
                border: '2px dashed var(--border-color)',
                borderRadius: 'var(--radius-sm)',
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                textAlign: 'center',
              }}
            >
              <input
                type="file"
                accept="audio/*"
                className="form-control"
                onChange={(e) => handleAudioChange(e.target.files[0] || null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                {trackAudioFile ? `Selected: ${trackAudioFile.name}` : 'Select .mp3, .wav, or .m4a file'}
              </div>
            </div>
            {audioPreviewUrl && (
              <div style={{ marginTop: '8px' }}>
                <audio controls src={audioPreviewUrl} style={{ width: '100%', height: '32px' }} />
              </div>
            )}
            {!trackAudioFile && (
              <div style={{ marginTop: '8px' }}>
                <input
                  type="url"
                  className="form-control"
                  placeholder="Or paste stream URL: https://.../sample.mp3"
                  value={trackAudioUrl}
                  onChange={(e) => setTrackAudioUrl(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Artwork Dropzone */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>🖼️ Cover Artwork Image</label>
            <div
              style={{
                border: '2px dashed var(--border-color)',
                borderRadius: 'var(--radius-sm)',
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                textAlign: 'center',
              }}
            >
              <input
                type="file"
                accept="image/*"
                className="form-control"
                onChange={(e) => handleImageChange(e.target.files[0] || null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                {trackCoverFile ? `Selected: ${trackCoverFile.name}` : 'Select .jpg or .png artwork'}
              </div>
            </div>
            {imagePreviewUrl && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={imagePreviewUrl} alt="Preview" style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }} />
                <span style={{ fontSize: '0.78rem', color: 'var(--neon-purple)', fontWeight: 700 }}>Artwork ready</span>
              </div>
            )}
            {!trackCoverFile && (
              <div style={{ marginTop: '8px' }}>
                <input
                  type="url"
                  className="form-control"
                  placeholder="Or paste artwork URL"
                  value={trackCoverImage}
                  onChange={(e) => setTrackCoverImage(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Genre Category */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Genre Category</label>
            <select
              className="form-control"
              value={trackGenre}
              onChange={(e) => setTrackGenre(e.target.value)}
            >
              <option value="HIP-HOP">Hip Hop / Trap</option>
              <option value="DESI-RAP">Desi Rap</option>
              <option value="TECHNO">Techno / DJ</option>
              <option value="ANTHEM">LOKA Anthem</option>
            </select>
          </div>

          {/* Submit Action */}
          <div style={{ gridColumn: '1 / -1', marginTop: '4px' }}>
            <button
              type="submit"
              className="btn btn-purple"
              disabled={isSubmitting}
              style={{ width: '100%', padding: '14px', fontSize: '0.92rem', borderRadius: '10px' }}
            >
              {isSubmitting ? <span className="spinner" /> : <><Sparkles size={18} /> PUBLISH TRACK TO MOBILE FLOATING BUBBLE</>}
            </button>
          </div>
        </form>
      </div>

      {/* Published Tracks Section in Sleek Cyber List Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
            Published Audio Tracks ({tracksList.length})
          </h3>
          <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '0.78rem' }} onClick={() => loadTracks && loadTracks()}>
            <RefreshCw size={13} /> Refresh List
          </button>
        </div>

        {tracksList.length === 0 ? (
          <div className="cyber-card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            No audio tracks published yet. Upload an MP3 file above to stream live across connected mobile apps!
          </div>
        ) : (
          <div className="cyber-list-container">
            {tracksList.map((tr, idx) => {
              const imgSrc = resolveAdminMediaUri(tr.image || tr.coverImage);
              const streamSrc = resolveAdminMediaUri(tr.audioUrl);

              return (
                <div
                  key={tr._id}
                  className="cyber-list-row list-row-animate"
                  style={{ animationDelay: `${idx * 0.06}s` }}
                >
                  {/* Left Column: Cover + Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 260px' }}>
                    <img
                      src={imgSrc}
                      alt={tr.title}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=300&q=80';
                      }}
                      style={{ width: '52px', height: '52px', borderRadius: '10px', objectFit: 'cover', border: '1px solid var(--border-color)', flexShrink: 0 }}
                    />
                    <div style={{ overflow: 'hidden' }}>
                      <div style={{ fontSize: '0.98rem', fontWeight: 800, color: 'var(--text-primary)' }} className="text-truncate">
                        {tr.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }} className="text-truncate">
                        {tr.artist || tr.artistName || 'Admin Featured Artist'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--neon-green)', fontWeight: 700, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="pulse-dot" /> Live Floating Player
                      </div>
                    </div>
                  </div>

                  {/* Middle Column: Audio Player Controls */}
                  <div style={{ flex: '1 1 300px', maxWidth: '380px', margin: '0 16px' }}>
                    {streamSrc && (
                      <audio controls src={streamSrc} style={{ width: '100%', height: '32px' }} />
                    )}
                  </div>

                  {/* Right Column: Genre & Delete Action */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                    <span className="badge badge-purple">{tr.genre || 'HIP-HOP'}</span>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                      onClick={() => handleDeleteTrack(tr._id, tr.title)}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
